import React, { useEffect, useRef, useState } from 'react';
import styles from '../register/register.module.scss';
import { Button, Loader, Typography } from 'src/components/common';
import { UploadIcon } from 'src/components/common/common.icons';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import ErrorMessage from 'src/components/common/formFields/errorMessage/ErrorMessage';
import PhoneNumberBox from 'src/components/common/formFields/PhoneNumberBox/PhoneNumberBox';
import { clearDemoDay, registerDemoDay } from 'src/store/venture/venture.slice';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { DemoDayBooking } from 'src/store/venture/venture.types';
import { openPopup } from 'src/store/common/common.slice';
import imageCompresser from 'src/components/common/image-compresser/imageCompresser';
import handleExtension from 'src/utils/handleDocumentNameExtension/handleDocumentNameExtension';
import { VentureAPIService } from 'src/services/venture.service';
import { CompanyAPIService } from 'src/services/company.service';
import FileCard from 'src/components/company/programs/fileCard/FileCard';
import { useUrlParamValue } from 'src/hooks';
import { componentSizeVariantsEnum } from 'src/constants';

const validationSchema = Yup.object().shape({
    pitchDeck: Yup.string()
        .url('Pitch deck must be a valid URL')
        .required('Pitch deck is required'),

    contactNo: Yup.string()
        .required('Contact number is required')
        .test('is-valid-indian-number', 'Enter a valid 10-digit number', function (value) {
            const onlyDigits = value?.replace(/\D/g, '') || '';
            return onlyDigits.startsWith('91') ? onlyDigits.length === 12 : onlyDigits.length === 10;
        })
});
interface TInitialValue {
    pitchDeck: string,
    contactNo: string
}
const initialValues: TInitialValue = {
    pitchDeck: '',
    contactNo: ''
}
export default function SubmitPitchDeck() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { paramValue } = useUrlParamValue();
    const { demoDay } = useAppSelector(
        (store) => store.venture
    );
    const ventureVerseId: string = paramValue({ paramName: 'id' }) as string;
    // state
    const [uploadLoading, setUploadLoading] = useState<boolean>(false);
    const [file, setFile] = useState(null)
    const [loading, setLoading] = useState<boolean>(false)
    const dragDropRef = useRef<HTMLDivElement>(null);
    const uploadImageRef = useRef<HTMLInputElement>(null);
    const openImageFileManager = async () => {
        if (uploadImageRef.current) {
            uploadImageRef.current.click();
        }
    };
    function handleNavigate() {
        navigate('/demo-day', { replace: true })
    }
    function handleSubmit(data: TInitialValue) {
        const payload: DemoDayBooking = {
            ventureVerseId,
            demoDayProgress: 'Pitch Deck',
            ...data
        }
        setLoading(true)
        dispatch(registerDemoDay(payload)).then((response) => {
            if (response?.data?.status) {
                dispatch(clearDemoDay())
                navigate(`/demo-day-register/slot-booking?id=${ventureVerseId}`, { replace: true })
            }
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            setLoading(false)
        })
    }
    async function handleUpload(files: FileList, type: string, setFieldValue: any) {
        const file = files[0];
        if (!file) return;
        if (file.size > 5485760) {
            return dispatch(openPopup({
                popupMessage: 'File size exceeds 5MB. Please upload a smaller file.',
                popupType: 'error'
            }))
        }
        const isDocs = file.type.startsWith('image/') || file.type.startsWith('video/');
        // Determine if the file is an image
        if (isDocs) {
            return dispatch(openPopup({
                popupMessage: 'Please upload a valid file type (PDF, DOC, DOCX, XLS, XLSX)',
                popupType: 'error'
            }));
        }
        setFile(file);
        setUploadLoading(true);

        try {
            const fileName = handleExtension(file.name.replace(/\s+/g, ''));
            // Obtain a presigned URL for upload
            const presignResponse: any = await new VentureAPIService().getPresignedUrl({
                ventureVerseId,
                fileName,
                fileType: type
            });

            const signedUrl = presignResponse?.data?.data?.signedUrl;
            if (!signedUrl) {
                throw new Error('Failed to obtain a presigned URL.');
            }

            // Read the file as an ArrayBuffer
            const reader = new FileReader();
            reader.readAsArrayBuffer(file);

            reader.onload = async () => {
                const data = reader.result;

                // Upload the file using the presigned URL
                const uploadResponse = await new CompanyAPIService().uploadCompanyProfilePicture({
                    presignedUrl: signedUrl,
                    data,
                    includeAuthorizationHeaders: false
                });

                if (uploadResponse.status === 200) {
                    // Retrieve the file key after upload
                    const keyResponse: any = await new VentureAPIService().getPresignedUrl({
                        ventureVerseId,
                        fileName,
                        fileType: type
                    });

                    const fileKey = keyResponse?.data?.data?.key;
                    if (fileKey) {
                        setFieldValue('pitchDeck', fileKey);
                    } else {
                        dispatch(openPopup({
                            popupMessage: 'Failed to retrieve the file key after upload.',
                            popupType: 'error'
                        }));
                    }
                } else {
                    dispatch(openPopup({
                        popupMessage: uploadResponse?.message || 'File upload failed.',
                        popupType: 'error'
                    }));
                }

                setUploadLoading(false);
            };

            reader.onerror = () => {
                throw new Error('Error reading the file.');
            };
        } catch (error: any) {
            setUploadLoading(false);
            dispatch(openPopup({
                popupMessage: error?.message || 'An unexpected error occurred during file upload.',
                popupType: 'error'
            }));
        }
    }

    useEffect(() => {
        if (demoDay && demoDay !== 'undefined' && demoDay?.demoDayProgress !== 'Register') {
            navigate('/demo-day')
        }
    }, [demoDay])

    return (
        <div className={styles.register}>
            <h2>Submit Your Pitch Deck</h2>
            <p>
                <strong>You&#39;ve taken the first step—</strong>now it&#39;s time to impress our panel. Only the <strong>best ideas</strong> will make it to the pitching stage!
            </p>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize
            >
                {({ errors, touched, setFieldValue, values }) => {
                    return (
                        <Form>
                            <div className={styles.upload}>
                                <div className='mb-3'>
                                    <PhoneNumberBox
                                        country="in"
                                        variant={componentSizeVariantsEnum.LARGE}
                                        value={values.contactNo}
                                        label="Contact number"
                                        isTouched={touched.contactNo}
                                        errorMessage={errors.contactNo}
                                        onChange={(e) => setFieldValue('contactNo', e.target.value)} />
                                </div>
                                <div className={styles.header}>
                                    <span className={styles.heading}>Pitch Deck</span>
                                    <span className={styles.description}>Check out this <a href='/sample_pitch_deck.pdf' download="sample-pitch-deck.pdf" className={styles.highlight}>Sample Pitch Deck</a> for inspiration.</span>
                                </div>
                                {!values.pitchDeck && <div
                                    className="dynamic-field__upload-file"
                                    style={{ background: '#fff', height: '60px', flexDirection: 'row', justifyContent: 'center' }}
                                    draggable
                                    ref={dragDropRef}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        handleUpload(e.dataTransfer.files, 'demo-day/pitch-deck', setFieldValue);
                                    }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    {!uploadLoading && <>
                                        <span className="dynamic-field__upload-file__drag-drop align-items-center" onClick={openImageFileManager}>
                                            <UploadIcon />
                                            <Typography as="p" variant="body-2" style={{ margin: 0, color: '#6E6E6E' }} fontWeight="regular">
                                                Drag and drop your pitch deck
                                            </Typography>
                                            <input
                                                type="file"
                                                hidden
                                                ref={uploadImageRef}
                                                accept=".pdf,.doc,.docx,.xls,.xlsx"
                                                className="select-post-image"
                                                onChange={(e) => {
                                                    handleUpload(e.target.files, 'demo-day/pitch-deck', setFieldValue);
                                                }}
                                            />
                                        </span>
                                        <Typography as="p" variant="body-2" style={{ margin: 0, color: '#6E6E6E' }} fontWeight="regular" onClick={openImageFileManager}>
                                            or
                                        </Typography>
                                        <Typography
                                            as="p"
                                            variant="body-2"
                                            style={{ margin: 0, color: '#fa4616' }}
                                            fontWeight="medium"
                                            className="dynamic-field__upload-file__browse"
                                            onClick={openImageFileManager}
                                        >
                                            Browse on your device
                                        </Typography>
                                    </>}
                                    {uploadLoading && <Loader />}
                                </div>}
                                {values.pitchDeck && <div className={styles.preview}>
                                    <FileCard
                                        fileSize={file?.size}
                                        fileName={file?.name}
                                        removeFile={() => {
                                            setFieldValue('pitchDeck', '')
                                            setFile(null)
                                        }}
                                        onClick={(e: any) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            try {
                                                window.open(values.pitchDeck);
                                            } catch (e) {
                                                console.log(e);
                                            }
                                        }}
                                    />
                                </div>}
                                {touched.pitchDeck && errors.pitchDeck && <ErrorMessage message={errors.pitchDeck} className='mt-3' />}
                            </div>
                            <p className={styles.desc}>We acknowledge that this pitch deck is confidential and is being provided exclusively for the purpose of evaluating a potential investment opportunity.</p>
                            <div className={styles.ctaButtons}>
                                <Button variant='secondary' size='small' style={{ width: 'auto', height: '50px', borderRadius: '47px' }} onClick={handleNavigate}>Cancel</Button>
                                <Button variant='primary' size='small' style={{ minWidth: '88.1px', width: 'auto', height: '50px', borderRadius: '47px' }} type='submit' disabled={uploadLoading} loading={loading}>Submit</Button>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
        </div>
    )
}
